import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { APIProvider } from "@vis.gl/react-google-maps";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { useDealDialogs } from "./ConfirmDialog";
import { BulkListBar } from "./BulkListBar";
import { DealsMap } from "./DealsMap";
import { FilterBar } from "./FilterBar";
import { LeadDetailModal } from "./LeadDetailModal";
import { LeadsTable } from "./LeadsTable";
import {
  inViewport,
  leadKey,
  sortLeads,
  type ViewportBounds,
} from "./leadUtils";
import {
  buildFilterOptions,
  filterAuctions,
  filterProbates,
  useDealFilters,
  useHidePastAuctionsMonths,
} from "./useDealFilters";
import { useHiddenLeads } from "./useHiddenLeads";
import { useLeadSelection } from "./useLeadSelection";
import { useAuctions, useProbates } from "./useLeads";
import { useListedLeadIds, useListMembers, useLists } from "./useLists";
import { usePolygonFilter } from "./usePolygonFilter";
import { useProbateGeocoding, withGeocodedCoords } from "./useProbateGeocoding";
import { useResolvedTheme } from "./useResolvedTheme";
import type { DealLead, LeadSortKey } from "./types";

const MAPS_KEY = import.meta.env.VITE_GOOGLE_MAPS_KEY as string | undefined;

const DealsContent = () => {
  const theme = useResolvedTheme();
  const { filters, set: setFilters } = useDealFilters();
  const hidePastMonths = useHidePastAuctionsMonths();

  const auctionsQuery = useAuctions();
  const probatesQuery = useProbates();
  const auctions = auctionsQuery.data ?? [];
  const geocoded = useProbateGeocoding(probatesQuery.data);
  const probates = useMemo(
    () => withGeocodedCoords(probatesQuery.data ?? [], geocoded),
    [probatesQuery.data, geocoded],
  );

  const { data: lists = [], invalidate: invalidateLists } = useLists();
  const { data: listMemberIds = null } = useListMembers(filters.list);
  const { data: listedLeadIds, invalidate: invalidateListedIds } =
    useListedLeadIds(filters.newOnly);

  const polygon = usePolygonFilter();
  const { hidden, hide, unhideAll } = useHiddenLeads();
  const selection = useLeadSelection();

  const [mapCollapsed, setMapCollapsed] = useState(
    () => window.matchMedia("(max-width: 768px)").matches,
  );
  const [activeLeadKey, setActiveLeadKey] = useState<string | null>(null);
  const [modalLead, setModalLead] = useState<DealLead | null>(null);

  // Current map viewport (debounced — onCameraChanged fires continuously
  // during pan/zoom). The table follows the map like a drawn area; markers
  // and auto-fit intentionally ignore it to avoid a zoom→filter→re-fit loop.
  const [viewportBounds, setViewportBounds] = useState<ViewportBounds | null>(
    null,
  );
  const boundsTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onBoundsChanged = useCallback((bounds: ViewportBounds) => {
    if (boundsTimer.current) clearTimeout(boundsTimer.current);
    boundsTimer.current = setTimeout(() => setViewportBounds(bounds), 250);
  }, []);
  useEffect(
    () => () => {
      if (boundsTimer.current) clearTimeout(boundsTimer.current);
    },
    [],
  );

  const ctx = {
    filters,
    hidePastAuctionsMonths: hidePastMonths,
    listMemberIds,
    insidePolygon: polygon.contains,
  };
  const filteredAuctions = useMemo(
    () => filterAuctions(auctions, ctx),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [auctions, filters, hidePastMonths, listMemberIds, polygon.contains],
  );
  const filteredProbates = useMemo(
    () => filterProbates(probates, ctx),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [probates, filters, listMemberIds, polygon.contains],
  );
  const filtered = useMemo(
    () => [...filteredAuctions, ...filteredProbates],
    [filteredAuctions, filteredProbates],
  );

  // Table pool: viewport, then hidden rows out, then "new only", then sort.
  // Viewport filtering is off while the map pane is collapsed (stale bounds).
  const { visible, hiddenCount, newOnlyExcluded } = useMemo(() => {
    const inView = mapCollapsed
      ? filtered
      : filtered.filter((l) => inViewport(l, viewportBounds));
    const notHidden = inView.filter((l) => !hidden.has(leadKey(l)));
    let pool = notHidden;
    if (filters.newOnly && listedLeadIds) {
      pool = pool.filter((l) => !listedLeadIds.has(String(l.id)));
    }
    return {
      visible: sortLeads(pool, filters.sortKey, filters.sortDir),
      hiddenCount: inView.length - notHidden.length,
      newOnlyExcluded:
        filters.newOnly && listedLeadIds
          ? notHidden.length - pool.length
          : null,
    };
  }, [
    filtered,
    hidden,
    filters.newOnly,
    filters.sortKey,
    filters.sortDir,
    listedLeadIds,
    mapCollapsed,
    viewportBounds,
  ]);

  const options = useMemo(
    () => buildFilterOptions(auctions, probates),
    [auctions, probates],
  );

  const countsLabel = auctionsQuery.error
    ? `❌ Failed to load auctions: ${(auctionsQuery.error as Error).message}`
    : [
        auctionsQuery.data ? `🏛️ ${filteredAuctions.length} auctions` : null,
        probatesQuery.data
          ? `⚖️ ${filteredProbates.length} probates (${
              filteredProbates.filter((p) => p.lat && p.lng).length
            } on map)`
          : null,
      ]
        .filter(Boolean)
        .join(" · ") || "Loading…";

  const selectedItems = useMemo(
    () => visible.filter((l) => selection.selected.has(leadKey(l))),
    [visible, selection.selected],
  );

  const { prompt, dialogs } = useDealDialogs();

  const onSort = (key: LeadSortKey) => {
    if (filters.sortKey === key) {
      setFilters({ sortDir: filters.sortDir === "asc" ? "desc" : "asc" });
    } else {
      setFilters({
        sortKey: key,
        sortDir: key === "value" || key === "date" ? "desc" : "asc",
      });
    }
  };

  return (
    <div className="flex h-full flex-col bg-background text-foreground">
      <FilterBar
        filters={filters}
        setFilters={setFilters}
        options={options}
        lists={lists}
        drawState={polygon.drawState}
        onDrawToggle={polygon.toggle}
        countsLabel={countsLabel}
      />
      <div className="relative flex min-h-0 flex-1">
        <div className="min-w-0 flex-1">
          <LeadsTable
            leads={visible}
            hiddenCount={hiddenCount}
            newOnlyExcluded={newOnlyExcluded}
            sortKey={filters.sortKey}
            sortDir={filters.sortDir}
            onSort={onSort}
            selected={selection.selected}
            onToggleSelect={selection.toggle}
            onSelectAll={selection.setAll}
            newOnly={filters.newOnly}
            onNewOnlyChange={(newOnly) => {
              selection.clear(); // selection may include rows about to hide
              setFilters({ newOnly });
            }}
            activeLeadKey={activeLeadKey}
            onRowClick={setModalLead}
            onHide={hide}
            onUnhideAll={unhideAll}
          />
        </div>
        <div
          className={cn(
            "relative border-l",
            mapCollapsed ? "hidden" : "w-[40%] min-w-[320px]",
          )}
        >
          {/* A Google Maps hard failure (quota/billing) throws from marker
              updates — contain it so the table keeps working. */}
          <ErrorBoundary
            fallback={
              <div className="flex h-full items-center justify-center p-6 text-center text-sm text-muted-foreground">
                Map unavailable (Google Maps failed to load). The table still
                works — reload to retry.
              </div>
            }
          >
            <DealsMap
              leads={filtered}
              theme={theme}
              activeLeadKey={activeLeadKey}
              onMarkerClick={(lead) => setActiveLeadKey(leadKey(lead))}
              onInfoClose={() => setActiveLeadKey(null)}
              onBoundsChanged={onBoundsChanged}
            />
          </ErrorBoundary>
        </div>
        <Button
          variant="secondary"
          size="icon"
          className="absolute right-2 top-2 z-10 rounded-full shadow"
          title={mapCollapsed ? "Show map" : "Hide map"}
          onClick={() => setMapCollapsed((c) => !c)}
        >
          {mapCollapsed ? "‹" : "›"}
        </Button>
      </div>
      {selectedItems.length > 0 && (
        <BulkListBar
          items={selectedItems}
          lists={lists}
          onListsChanged={invalidateLists}
          onListedIdsChanged={invalidateListedIds}
          onClear={selection.clear}
          prompt={prompt}
        />
      )}
      <LeadDetailModal
        item={modalLead}
        visibleLeads={visible}
        onNavigate={setModalLead}
        onClose={() => setModalLead(null)}
      />
      {dialogs}
    </div>
  );
};

/**
 * Deals — the DealEngine map/leads tool, now a native React page (the old
 * /map.html iframe is gone). Renders full-bleed below the app header: the
 * Layout centers <main> at max-w-screen-xl, so the page breaks out with a
 * fixed container whose top edge tracks the measured header height.
 */
export const DealsPage = () => {
  const [top, setTop] = useState(48);

  useEffect(() => {
    const measure = () => {
      const header = document.querySelector("header");
      if (header) setTop(Math.round(header.getBoundingClientRect().height));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  if (!MAPS_KEY) {
    return (
      <div className="py-10 text-center text-muted-foreground">
        Google Maps key missing — set VITE_GOOGLE_MAPS_KEY.
      </div>
    );
  }

  return (
    <div
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        top,
        bottom: 0,
        zIndex: 0,
      }}
    >
      <APIProvider apiKey={MAPS_KEY}>
        <DealsContent />
      </APIProvider>
    </div>
  );
};

DealsPage.path = "/deals";
